import type { Route, MoveInput } from './types';
import type { Simulation } from './simulation';
export type RouteAnalysis = {
    name: string;
    exact: boolean;
    error: number;
    grabsMatch: boolean;
    trials: number;
    successes: number;
    releaseWindowMs: number;
    maxDeviation: number;
};
/** Authoring probes replay inputs in an isolated slot; never edits the player state. */
export function analyzeRoute(sim: Simulation, route: Route, perturb = false): RouteAnalysis {
    if (!route.inputs || !route.initialState || !route.finalState)
        throw Error('Record a route before analyzing it.');
    if (route.physicsVersion !== sim.version)
        throw Error('Re-record this route with the current physics.');
    const run = (inputs: MoveInput[]) => { sim.restore(route.initialState!, 29); for (const input of inputs)
        sim.step(29, input); const end = sim.read(29); return { error: Math.hypot(...end.p.map((p, i) => p - route.finalState![i])), end }; };
    const baseline = run(route.inputs), grabsMatch = baseline.end.grabs === route.finalState[19] && baseline.end.tails === route.finalState[20];
    const result: RouteAnalysis = { name: route.name, exact: baseline.error < .08 && grabsMatch, error: baseline.error, grabsMatch, trials: 0, successes: 0, releaseWindowMs: 0, maxDeviation: baseline.error };
    if (!perturb)
        return result;
    const releases: number[] = [];
    for (let i = 1; i < route.inputs.length; i++)
        if ((route.inputs[i - 1].buttons & 3) && (!(route.inputs[i].buttons & 3) || (route.inputs[i].buttons & 16)))
            releases.push(i);
    for (const ticks of [-6, -3, 0, 3, 6])
        for (const scale of [.9, 1, 1.1]) {
            if (ticks === 0 && scale === 1)
                continue;
            const inputs = route.inputs.map(a => ({ ...a, x: a.x * scale, z: a.z * scale }));
            for (const release of releases) {
                const first = Math.max(0, release + Math.min(0, ticks)), last = Math.min(inputs.length, release + Math.max(0, ticks));
                for (let i = first; i < last; i++)
                    inputs[i].buttons = ticks < 0 ? route.inputs[release].buttons : route.inputs[release - 1].buttons;
            }
            const test = run(inputs);
            result.trials++;
            result.maxDeviation = Math.max(result.maxDeviation, test.error);
            if (test.error < 1.5 && test.end.mode === route.finalState[15]) {
                result.successes++;
                if (scale === 1)
                    result.releaseWindowMs = Math.max(result.releaseWindowMs, Math.abs(ticks) / 120 * 1000);
            }
        }
    return result;
}
